import { useEffect, useState, useCallback, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';
const getToken = () => localStorage.getItem('ozon_token');

async function cfReq(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers };
  const res = await fetch(`${BASE_URL}/company-finance${path}`, { ...options, headers });
  if (!res.ok) { const e = await res.json().catch(() => ({ error: res.statusText })); throw new Error(e.error || 'Hata'); }
  return res.json();
}

const SYM   = { TRY: '₺', USD: '$', EUR: '€', RUB: '₽' };
const CURR  = ['TRY', 'USD', 'EUR', 'RUB'];
const fmt   = (n, c = 'TRY') => `${SYM[c] || c}${Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
const fmtD  = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';
const isoD  = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';
const today = () => new Date().toISOString().slice(0, 10);
const MONTHS = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

const PM_ICON = { cash: '💵', card: '💳', bank: '🏦', safe: '🏛️' };

// Fallback — API yüklenemezse bu statik liste kullanılır
const DEFAULT_TX_TYPES = [
  { key: 'gelir',        label: 'Gelir',        grp: 'gelir',  active: true, is_system: true, sort_order: 1 },
  { key: 'borc_alindi',  label: 'Borç Alındı',  grp: 'gelir',  active: true, is_system: true, sort_order: 2 },
  { key: 'avans_alindi', label: 'Avans Alındı', grp: 'gelir',  active: true, is_system: true, sort_order: 3 },
  { key: 'diger_gelir',  label: 'Diğer Gelir',  grp: 'gelir',  active: true, is_system: true, sort_order: 4 },
  { key: 'gider',        label: 'Gider',        grp: 'gider',  active: true, is_system: true, sort_order: 1 },
  { key: 'borc_odendi',  label: 'Borç Ödendi',  grp: 'gider',  active: true, is_system: true, sort_order: 2 },
  { key: 'taksit',       label: 'Taksit',       grp: 'gider',  active: true, is_system: true, sort_order: 3 },
  { key: 'vade_farki',   label: 'Vade Farkı',   grp: 'gider',  active: true, is_system: true, sort_order: 4 },
  { key: 'faiz',         label: 'Faiz',         grp: 'gider',  active: true, is_system: true, sort_order: 5 },
  { key: 'diger_gider',  label: 'Diğer Gider',  grp: 'gider',  active: true, is_system: true, sort_order: 6 },
];

// Sistem tx_type renk/ikon haritası
const TYPE_CFG = {
  gelir:        { label: 'Gelir',        color: 'green',  sign: '+', income: true  },
  borc_alindi:  { label: 'Borç Alındı',  color: 'orange', sign: '+', income: true  },
  avans_alindi: { label: 'Avans Alındı', color: 'green',  sign: '+', income: true  },
  diger_gelir:  { label: 'Diğer Gelir',  color: 'green',  sign: '+', income: true  },
  gider:        { label: 'Gider',        color: 'red',    sign: '-', income: false },
  borc_odendi:  { label: 'Borç Ödendi',  color: 'blue',   sign: '-', income: false },
  taksit:       { label: 'Taksit',       color: 'blue',   sign: '-', income: false },
  vade_farki:   { label: 'Vade Farkı',   color: 'red',    sign: '-', income: false },
  faiz:         { label: 'Faiz',         color: 'red',    sign: '-', income: false },
  diger_gider:  { label: 'Diğer Gider',  color: 'red',    sign: '-', income: false },
};

function txCfg(t, txTypes = []) {
  const sys = TYPE_CFG[t.tx_type];
  if (sys) return sys;
  // Özel tür
  const custom = txTypes.find(x => x.key === t.tx_type);
  if (custom) return {
    label: custom.label,
    color: custom.grp === 'gelir' ? 'green' : 'red',
    sign:  custom.grp === 'gelir' ? '+' : '-',
    income: custom.grp === 'gelir',
  };
  return TYPE_CFG[t.direction === 'in' ? 'gelir' : 'gider'];
}

// ─── Ortak bileşenler ─────────────────────────────────────────────────────────
function Badge({ children, color = 'gray' }) {
  const cls = {
    green:  'bg-green-100 text-green-700',
    red:    'bg-red-100 text-red-600',
    yellow: 'bg-yellow-100 text-yellow-700',
    blue:   'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-orange-700',
    gray:   'bg-gray-100 text-gray-600',
  }[color] || 'bg-gray-100 text-gray-600';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{children}</span>;
}
function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>{children}</div>;
}
function Input({ label, ...props }) {
  return (
    <div>
      {label && <label className="text-xs text-gray-400 block mb-1">{label}</label>}
      <input {...props} className={`w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${props.className || ''}`} />
    </div>
  );
}
function Select({ label, children, ...props }) {
  return (
    <div>
      {label && <label className="text-xs text-gray-400 block mb-1">{label}</label>}
      <select {...props} className={`w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 ${props.className || ''}`}>
        {children}
      </select>
    </div>
  );
}

// ─── Dosya Ekleri (mevcut kayıt için) ────────────────────────────────────────
function Attachments({ entityId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const ref = useRef();
  const load = useCallback(async () => {
    if (!entityId) return;
    try { setFiles(await cfReq(`/attachments?entity_type=transaction&entity_id=${entityId}`)); } catch (_) {}
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  async function upload(e) {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('entity_type', 'transaction'); fd.append('entity_id', entityId);
      const res = await fetch(`${BASE_URL}/company-finance/attachments/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      });
      if (!res.ok) throw new Error((await res.json()).error);
      load();
    } catch (e) { alert(e.message); } finally { setUploading(false); if (ref.current) ref.current.value = ''; }
  }
  async function del(id) {
    if (!window.confirm('Belgeyi sil?')) return;
    await cfReq(`/attachments/${id}`, { method: 'DELETE' }); load();
  }

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs text-gray-400">📎 {files.length > 0 ? `${files.length} belge` : 'Belge yok'}</span>
        <button onClick={() => ref.current?.click()} disabled={uploading}
          className="text-xs text-blue-500 hover:text-blue-700 disabled:opacity-50">
          {uploading ? 'Yükleniyor…' : '+ Belge Ekle'}
        </button>
        <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={upload} className="hidden" />
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">
              <span className="text-xs">{f.mime_type?.includes('pdf') ? '📄' : '🖼️'}</span>
              <a href={`${BASE_URL}/company-finance/attachments/${f.id}/download`} target="_blank" rel="noreferrer"
                className="text-xs text-blue-600 hover:underline max-w-[120px] truncate">{f.original_name}</a>
              <button onClick={() => del(f.id)} className="text-gray-300 hover:text-red-400 text-xs ml-0.5">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dosya seçici (yeni kayıt için) ──────────────────────────────────────────
function FilePicker({ files, onChange }) {
  const ref = useRef();
  function add(e) {
    const f = e.target.files[0]; if (!f) return;
    onChange([...files, f]);
    if (ref.current) ref.current.value = '';
  }
  function remove(idx) { onChange(files.filter((_, i) => i !== idx)); }
  return (
    <div>
      <label className="text-xs text-gray-400 block mb-1">Belgeler</label>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {files.map((f, i) => (
          <div key={i} className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded px-2 py-0.5">
            <span className="text-xs">{f.type?.includes('pdf') ? '📄' : '🖼️'}</span>
            <span className="text-xs text-blue-700 max-w-[120px] truncate">{f.name}</span>
            <button type="button" onClick={() => remove(i)} className="text-blue-300 hover:text-red-400 text-xs ml-0.5">✕</button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()}
          className="text-xs text-blue-500 hover:text-blue-700 border border-dashed border-blue-200 px-2 py-0.5 rounded">
          📎 Dosya Ekle
        </button>
      </div>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={add} className="hidden" />
    </div>
  );
}

// ─── Borç Ödeme Formu ─────────────────────────────────────────────────────────
function DebtPayForm({ paymentMethods, onSaved, onCancel }) {
  const [debts, setDebts]       = useState([]);
  const [loadingDebts, setLd]   = useState(true);
  const [selectedDebt, setSel]  = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({
    date: today(), amount: '', payment_method_id: '', faiz_pct: '', vade_farki_amt: '', note: '',
  });
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    cfReq('/debts/open').then(setDebts).catch(() => {}).finally(() => setLd(false));
  }, []);

  function selectDebt(debt) {
    setSel(debt);
    setForm(p => ({ ...p, amount: (+debt.remaining).toFixed(2) }));
  }

  const principal = parseFloat(form.amount) || 0;
  const faizPct   = parseFloat(form.faiz_pct) || 0;
  const faizAmt   = +(principal * faizPct / 100).toFixed(2);
  const vadeFarki = parseFloat(form.vade_farki_amt) || 0;
  const totalOut  = +(principal + faizAmt + vadeFarki).toFixed(2);

  async function uploadFiles(txId) {
    for (const file of pendingFiles) {
      const fd = new FormData();
      fd.append('file', file); fd.append('entity_type', 'transaction'); fd.append('entity_id', txId);
      await fetch(`${BASE_URL}/company-finance/attachments/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      }).catch(() => {});
    }
  }

  async function save() {
    if (!selectedDebt) return alert('Hangi borcun ödendiğini seçin');
    if (!principal)     return alert('Tutar giriniz');
    setSaving(true);
    try {
      const base = {
        date: form.date, currency: selectedDebt.currency,
        counterparty: selectedDebt.counterparty,
        payment_method_id: form.payment_method_id || null,
        note: form.note || null,
        source_tx_id: selectedDebt.id,
      };
      const mainTx = await cfReq('/transactions', { method: 'POST', body: JSON.stringify({
        ...base, tx_type: 'borc_odendi', amount: principal,
        description: `Borç ödemesi${selectedDebt.counterparty ? ' — ' + selectedDebt.counterparty : ''}`,
      })});
      await uploadFiles(mainTx.id);
      if (faizAmt > 0) {
        await cfReq('/transactions', { method: 'POST', body: JSON.stringify({
          ...base, tx_type: 'faiz', amount: faizAmt, interest_rate: faizPct,
          description: `Faiz — ${faizPct}%${selectedDebt.counterparty ? ' — ' + selectedDebt.counterparty : ''}`,
        })});
      }
      if (vadeFarki > 0) {
        await cfReq('/transactions', { method: 'POST', body: JSON.stringify({
          ...base, tx_type: 'vade_farki', amount: vadeFarki,
          description: `Vade farkı${selectedDebt.counterparty ? ' — ' + selectedDebt.counterparty : ''}`,
        })});
      }
      onSaved(`Ödeme kaydedildi: ${fmt(totalOut, selectedDebt.currency)}`);
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  }

  return (
    <Card className="p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">💙 Borç Ödemesi</h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>

      <div className="mb-4">
        <label className="text-xs text-gray-400 block mb-2 font-medium">Hangi borç ödeniyor?</label>
        {loadingDebts ? (
          <div className="text-sm text-gray-400 py-3 text-center">Yükleniyor…</div>
        ) : debts.length === 0 ? (
          <div className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-lg">Açık borç kaydı yok</div>
        ) : (
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {debts.map(d => (
              <button key={d.id} type="button" onClick={() => selectDebt(d)}
                className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                  selectedDebt?.id === d.id
                    ? 'border-blue-400 bg-blue-50 text-blue-800'
                    : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/50 text-gray-700'
                }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold">{d.counterparty || 'İsimsiz'}</span>
                    {d.description && <span className="text-gray-400 ml-2 text-xs">{d.description}</span>}
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Toplam: {fmt(d.amount, d.currency)}</div>
                    <div className={`font-bold ${+d.remaining < +d.amount ? 'text-orange-600' : 'text-red-600'}`}>
                      Kalan: {fmt(d.remaining, d.currency)}
                    </div>
                    {d.due_date && <div className="text-xs text-orange-500">📅 {fmtD(d.due_date)}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedDebt && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-3">
            <Input label="Tarih" type="date" value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
            <Input label={`Ödenen Tutar (${selectedDebt.currency})`} type="number" step="0.01" min="0"
              value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
            <Input label="Faiz %" type="number" step="0.1" min="0" placeholder="0"
              value={form.faiz_pct} onChange={e => setForm(p => ({ ...p, faiz_pct: e.target.value }))} />
            <Input label="Vade Farkı Tutarı" type="number" step="0.01" min="0" placeholder="0.00"
              value={form.vade_farki_amt} onChange={e => setForm(p => ({ ...p, vade_farki_amt: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-2.5 mb-3">
            <Select label="Ödeme Yöntemi" value={form.payment_method_id}
              onChange={e => setForm(p => ({ ...p, payment_method_id: e.target.value }))}>
              <option value="">— Seç —</option>
              {paymentMethods.filter(m => m.active).map(m => (
                <option key={m.id} value={m.id}>{PM_ICON[m.type]} {m.name}</option>
              ))}
            </Select>
            <Input label="Not" placeholder="Opsiyonel…" value={form.note}
              onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
          </div>

          <div className="mb-3">
            <FilePicker files={pendingFiles} onChange={setPendingFiles} />
          </div>

          {(faizAmt > 0 || vadeFarki > 0) && (
            <div className="flex gap-4 flex-wrap bg-orange-50 border border-orange-100 rounded-lg px-4 py-2 mb-3 text-xs">
              <span>Anapara: <strong>{fmt(principal, selectedDebt.currency)}</strong></span>
              {faizAmt > 0 && <span className="text-orange-600">+ Faiz ({faizPct}%): <strong>{fmt(faizAmt, selectedDebt.currency)}</strong></span>}
              {vadeFarki > 0 && <span className="text-orange-600">+ Vade Farkı: <strong>{fmt(vadeFarki, selectedDebt.currency)}</strong></span>}
              <span className="font-bold text-gray-800">= Toplam: {fmt(totalOut, selectedDebt.currency)}</span>
            </div>
          )}

          <button onClick={save} disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg text-sm font-semibold">
            {saving ? '…' : `✅ Öde — ${fmt(totalOut, selectedDebt.currency)}`}
          </button>
        </>
      )}
    </Card>
  );
}

// ─── Kayıt Formu ──────────────────────────────────────────────────────────────
function EntryForm({ txTypes, categories, paymentMethods, onSaved, editing, onCancel }) {
  const blank = {
    tx_type: 'gider', date: today(), description: '', amount: '', currency: 'TRY',
    category_id: '', payment_method_id: '', counterparty: '', due_date: '', note: '',
  };
  const [open, setOpen]         = useState(!!editing);
  const [saving, setSave]       = useState(false);
  const [form, setForm]         = useState(blank);
  const [pendingFiles, setPF]   = useState([]);

  useEffect(() => {
    if (editing) {
      setForm({
        tx_type: editing.tx_type || (editing.direction === 'in' ? 'gelir' : 'gider'),
        date: isoD(editing.date), description: editing.description || '',
        amount: editing.amount || '', currency: editing.currency || 'TRY',
        category_id: editing.category_id || '', payment_method_id: editing.payment_method_id || '',
        counterparty: editing.counterparty || '', due_date: isoD(editing.due_date), note: editing.note || '',
      });
      setOpen(true);
    }
  }, [editing]);

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  // Seçili türün grup bilgisi
  const typeDef = txTypes.find(t => t.key === form.tx_type) || { grp: 'gider' };
  const isIncome  = typeDef.grp === 'gelir';
  const isDebt    = form.tx_type === 'borc_alindi';
  const cats      = categories.filter(c => c.type === (isIncome ? 'income' : 'expense'));

  const gelirTypes  = txTypes.filter(t => t.grp === 'gelir' && t.active);
  const giderTypes  = txTypes.filter(t => t.grp === 'gider' && t.active);

  async function uploadFiles(txId) {
    for (const file of pendingFiles) {
      const fd = new FormData();
      fd.append('file', file); fd.append('entity_type', 'transaction'); fd.append('entity_id', txId);
      await fetch(`${BASE_URL}/company-finance/attachments/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      }).catch(() => {});
    }
  }

  async function save() {
    if (!form.date || !form.amount) return alert('Tarih ve tutar zorunlu');
    setSave(true);
    try {
      const payload = {
        ...form,
        direction:         isIncome ? 'in' : 'out',  // özel türler için
        category_id:       form.category_id       || null,
        payment_method_id: form.payment_method_id || null,
        counterparty:      form.counterparty       || null,
        due_date:          form.due_date           || null,
        note:              form.note               || null,
        debt_status:       isDebt ? 'unpaid' : null,
      };
      if (editing) {
        await cfReq(`/transactions/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        const newTx = await cfReq('/transactions', { method: 'POST', body: JSON.stringify(payload) });
        await uploadFiles(newTx.id);
        setForm(blank); setPF([]); setOpen(false);
      }
      onSaved();
    } catch (e) { alert(e.message); } finally { setSave(false); }
  }

  if (!open && !editing) return (
    <button onClick={() => setOpen(true)}
      className="flex items-center gap-2 w-full border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50/30 rounded-xl px-4 py-3 text-sm text-gray-400 hover:text-blue-600 transition-colors mb-2">
      ➕ Yeni kayıt ekle
    </button>
  );

  return (
    <Card className="p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-700 text-sm">{editing ? '✏️ Düzenle' : '➕ Yeni Kayıt'}</h3>
        {!editing && <button onClick={() => { setOpen(false); setPF([]); }} className="text-gray-400 hover:text-gray-600">✕</button>}
      </div>

      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 mb-3">
        {/* İşlem Türü — grouped select */}
        <div className="col-span-2 md:col-span-1">
          <label className="text-xs text-gray-400 block mb-1">İşlem Türü</label>
          <select value={form.tx_type}
            onChange={e => setForm(p => ({ ...p, tx_type: e.target.value, category_id: '' }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
            {gelirTypes.length > 0 && (
              <optgroup label="📈 Gelir">
                {gelirTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </optgroup>
            )}
            {giderTypes.length > 0 && (
              <optgroup label="📉 Gider">
                {giderTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </optgroup>
            )}
          </select>
        </div>

        <Input label="Tarih" type="date" value={form.date} onChange={f('date')} />

        <div>
          <label className="text-xs text-gray-400 block mb-1">Tutar</label>
          <div className="flex gap-1">
            <input type="number" min="0" step="0.01" value={form.amount} onChange={f('amount')} placeholder="0.00"
              className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
            <select value={form.currency} onChange={f('currency')}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
              {CURR.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div className="col-span-2 md:col-span-3">
          <Input label="Açıklama" value={form.description} onChange={f('description')} placeholder="Ne için?" />
        </div>

        {cats.length > 0 && (
          <Select label="Kategori" value={form.category_id} onChange={f('category_id')}>
            <option value="">— Seç —</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        )}

        <Select label="Ödeme Yöntemi" value={form.payment_method_id} onChange={f('payment_method_id')}>
          <option value="">— Seç —</option>
          {['cash','card','bank','safe'].map(type => {
            const ms = paymentMethods.filter(m => m.type === type && m.active);
            if (!ms.length) return null;
            return <optgroup key={type} label={`${PM_ICON[type]} ${type}`}>{ms.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</optgroup>;
          })}
        </Select>

        {isDebt && <Input label="Alacaklı (kime borçlu?)" value={form.counterparty} onChange={f('counterparty')} placeholder="Kişi / Banka…" />}
        {isDebt && <Input label="Vade Tarihi" type="date" value={form.due_date} onChange={f('due_date')} />}

        <div className={`${isDebt ? 'col-span-2 md:col-span-3' : 'col-span-2 md:col-span-1'}`}>
          <Input label="Not" value={form.note} onChange={f('note')} placeholder="Opsiyonel…" />
        </div>

        {/* Dosya ekleme (sadece yeni kayıtta) */}
        {!editing && (
          <div className="col-span-2 md:col-span-3">
            <FilePicker files={pendingFiles} onChange={setPF} />
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={save} disabled={saving}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-5 py-1.5 rounded-lg text-sm font-semibold">
          {saving ? '…' : editing ? 'Güncelle' : 'Kaydet'}
        </button>
        {editing && <button onClick={onCancel} className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg text-sm">İptal</button>}
      </div>
    </Card>
  );
}

// ─── Defter Sekmesi ───────────────────────────────────────────────────────────
function LedgerTab({ txTypes, categories }) {
  const now = new Date();
  const [year,  setYear]  = useState(0);                 // 0 = tüm yıllar
  const [month, setMonth] = useState(0);                 // 0 = tüm aylar
  const [txns, setTxns]   = useState([]);
  const [pms, setPms]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDebtPay, setShowDebtPay] = useState(false);
  const [expandedId, setExpandedId]   = useState(null);
  const [toast, setToast] = useState('');
  const YEARS = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (year)  params.push(`year=${year}`);
      if (month) params.push(`month=${month}`);
      const url = `/transactions${params.length ? '?' + params.join('&') : ''}`;
      const [t, pm] = await Promise.all([cfReq(url), cfReq('/payment-methods')]);
      setTxns(t); setPms(pm);
    } catch (_) {} finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 4000); }
  async function del(id) {
    if (!window.confirm('Kaydı sil?')) return;
    await cfReq(`/transactions/${id}`, { method: 'DELETE' }); load();
  }

  return (
    <div>
      {toast && (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-2.5 mb-3 text-sm text-green-700">
          <span>✅ {toast}</span>
          <button onClick={() => setToast('')} className="text-green-400 hover:text-green-600 ml-4">✕</button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={year} onChange={e => { setYear(+e.target.value); setMonth(0); }}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
          <option value={0}>📅 Tüm Zamanlar</option>
          {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {year > 0 && (
          <select value={month} onChange={e => setMonth(+e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
            <option value={0}>Tüm Yıl</option>
            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        )}
        <div className="flex-1" />
        <button onClick={() => { setShowDebtPay(p => !p); setEditing(null); }}
          className={`text-sm px-4 py-1.5 rounded-lg border font-medium transition-colors ${
            showDebtPay ? 'bg-blue-600 text-white border-blue-600' : 'border-blue-300 text-blue-600 hover:bg-blue-50'
          }`}>
          💙 Borç Öde
        </button>
      </div>

      {showDebtPay && (
        <DebtPayForm
          paymentMethods={pms}
          onSaved={(msg) => { setShowDebtPay(false); showToast(msg); load(); }}
          onCancel={() => setShowDebtPay(false)}
        />
      )}

      {!showDebtPay && (
        <EntryForm
          txTypes={txTypes}
          categories={categories}
          paymentMethods={pms}
          onSaved={load}
          editing={editing}
          onCancel={() => setEditing(null)}
        />
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Yükleniyor…</div>
      ) : txns.length === 0 ? (
        <div className="text-center py-12 text-gray-400">Bu dönemde kayıt yok</div>
      ) : (
        <>
          {/* ── MOBİL: Kart listesi ── */}
          <div className="md:hidden space-y-2">
            {txns.map(t => {
              const cfg   = txCfg(t, txTypes);
              const isExp = expandedId === t.id;
              return (
                <div key={t.id}
                  onClick={() => setExpandedId(isExp ? null : t.id)}
                  className={`bg-white border rounded-xl p-3 cursor-pointer transition-colors
                    ${t.debt_status === 'overdue' ? 'border-red-200 bg-red-50/20' : 'border-gray-100'}`}>
                  {/* Üst: tür + tutar */}
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge color={cfg.color}>{cfg.label}</Badge>
                      {t.debt_status && t.debt_status !== 'paid' && (
                        <Badge color={t.debt_status === 'overdue' ? 'red' : t.debt_status === 'partial' ? 'blue' : 'yellow'}>
                          {t.debt_status === 'overdue' ? 'Gecikmiş' : t.debt_status === 'partial' ? 'Kısmi' : 'Bekliyor'}
                        </Badge>
                      )}
                    </div>
                    <span className={`text-sm font-bold flex-shrink-0 ${cfg.income ? 'text-green-600' : 'text-red-500'}`}>
                      {cfg.sign}{fmt(t.amount, t.currency)}
                    </span>
                  </div>
                  {/* Alt: açıklama + tarih */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-gray-600 min-w-0">
                      {t.counterparty && <span className="font-medium text-gray-800 mr-1">{t.counterparty}</span>}
                      <span className="text-gray-500">{t.description || '—'}</span>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">{fmtD(t.date)}</span>
                  </div>
                  {t.due_date && t.debt_status !== 'paid' && (
                    <div className="text-xs text-orange-500 mt-1">📅 Vade: {fmtD(t.due_date)}</div>
                  )}
                  {/* Genişletilmiş */}
                  {isExp && (
                    <div className="mt-2 pt-2 border-t border-gray-100" onClick={e => e.stopPropagation()}>
                      {t.note && <p className="text-xs text-gray-500 mb-2">📝 {t.note}</p>}
                      {t.interest_rate && <p className="text-xs text-gray-500 mb-2">Faiz oranı: %{t.interest_rate}</p>}
                      <Attachments entityId={t.id} />
                      <div className="flex gap-3 mt-2">
                        <button onClick={() => { setEditing(t); setShowDebtPay(false); }}
                          className="text-xs text-blue-500 hover:text-blue-700 font-medium">✏️ Düzenle</button>
                        <button onClick={() => del(t.id)}
                          className="text-xs text-red-400 hover:text-red-600 font-medium">🗑️ Sil</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── MASAÜSTÜ: Tablo ── */}
          <Card className="overflow-hidden hidden md:block">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase w-24">Tarih</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase">Tür</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase">Açıklama</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase">Tutar</th>
                  <th className="px-4 py-2.5 w-32"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {txns.map(t => {
                  const cfg   = txCfg(t, txTypes);
                  const isExp = expandedId === t.id;
                  return (
                    <>
                      <tr key={t.id}
                        onClick={() => setExpandedId(isExp ? null : t.id)}
                        className={`hover:bg-gray-50/60 cursor-pointer ${t.debt_status === 'overdue' ? 'bg-red-50/30' : ''}`}>
                        <td className="px-4 py-2.5 text-sm text-gray-500 whitespace-nowrap">{fmtD(t.date)}</td>
                        <td className="px-4 py-2.5">
                          <Badge color={cfg.color}>{cfg.label}</Badge>
                          {t.debt_status && t.debt_status !== 'paid' && (
                            <span className="ml-1">
                              <Badge color={t.debt_status === 'overdue' ? 'red' : t.debt_status === 'partial' ? 'blue' : 'yellow'}>
                                {t.debt_status === 'overdue' ? 'Gecikmiş' : t.debt_status === 'partial' ? 'Kısmi' : 'Bekliyor'}
                              </Badge>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-700">
                          {t.counterparty && <span className="font-medium text-gray-800 mr-1">{t.counterparty}</span>}
                          <span className="text-gray-500">{t.description || '—'}</span>
                          {t.due_date && t.debt_status !== 'paid' && (
                            <span className="ml-2 text-xs text-orange-500">📅 {fmtD(t.due_date)}</span>
                          )}
                        </td>
                        <td className={`px-4 py-2.5 text-sm font-semibold text-right ${cfg.income ? 'text-green-600' : 'text-red-500'}`}>
                          {cfg.sign}{fmt(t.amount, t.currency)}
                        </td>
                        <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => { setEditing(t); setShowDebtPay(false); }}
                              className="text-xs text-blue-400 hover:text-blue-600">Düzenle</button>
                            <button onClick={() => del(t.id)}
                              className="text-xs text-red-300 hover:text-red-500">Sil</button>
                          </div>
                        </td>
                      </tr>
                      {isExp && (
                        <tr key={`${t.id}-d`} className="bg-blue-50/20">
                          <td colSpan={5} className="px-6 py-3">
                            {t.note && <p className="text-xs text-gray-500 mb-2">📝 {t.note}</p>}
                            {t.interest_rate && <p className="text-xs text-gray-500 mb-2">Faiz oranı: %{t.interest_rate}</p>}
                            <Attachments entityId={t.id} />
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Genel Bakış ─────────────────────────────────────────────────────────────
function OverviewTab() {
  const [data, setData]       = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([cfReq('/overview'), cfReq('/summary')])
      .then(([ov, s]) => { setData(ov); setSummary(s); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-16 text-gray-400">Yükleniyor…</div>;
  if (!data)   return <div className="text-center py-16 text-gray-400">Veri alınamadı</div>;

  const chartData = {};
  for (const r of data.monthly) {
    if (r.currency !== 'TRY') continue;
    if (!chartData[r.month]) chartData[r.month] = { month: r.month.slice(5)+'/'+r.month.slice(0,4), income: 0, expense: 0 };
    chartData[r.month].income  += +r.income;
    chartData[r.month].expense += +r.expense;
  }
  const chartArr = Object.values(chartData).slice(-8);

  return (
    <div className="space-y-5">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ['Toplam Gelir',  summary.total_income,  'text-green-600',  'bg-green-50',  '▲'],
            ['Toplam Gider',  summary.total_expense, 'text-red-500',    'bg-red-50',    '▼'],
            ['Net Bakiye',    summary.net_balance,   summary.net_balance >= 0 ? 'text-blue-600' : 'text-red-600', 'bg-blue-50', '='],
            ['Açık Borç',     summary.open_debt,     summary.open_debt > 0 ? 'text-orange-600' : 'text-green-600', 'bg-orange-50', '🤝'],
          ].map(([lbl, val, textCls, bgCls, icon]) => (
            <div key={lbl} className={`${bgCls} rounded-xl p-4`}>
              <div className="text-xs text-gray-500 mb-1">{lbl}</div>
              <div className={`text-xl font-bold ${textCls}`}>{icon} {fmt(val)}</div>
            </div>
          ))}
        </div>
      )}
      {data.upcomingLoans?.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">🔴 Açık Borçlar</h3>
          <div className="space-y-2">
            {data.upcomingLoans.map((l, i) => (
              <div key={i} className="flex items-center gap-3 text-sm py-1">
                <span className="font-medium text-gray-700 flex-1">{l.person_name || '—'}</span>
                <span className="font-semibold text-red-600">{fmt(l.amount, l.currency)}</span>
                {l.due_date && <span className="text-xs text-orange-500">📅 {fmtD(l.due_date)}</span>}
                <Badge color={l.status === 'overdue' ? 'red' : l.status === 'partial' ? 'blue' : 'yellow'}>
                  {l.status === 'overdue' ? 'Gecikmiş' : l.status === 'partial' ? 'Kısmi' : 'Bekliyor'}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
      {chartArr.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-gray-600 mb-3">📊 Son 8 Ay (TRY)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartArr}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v, n) => [fmt(v), n === 'income' ? 'Gelir' : 'Gider']} />
              <Legend formatter={v => v === 'income' ? 'Gelir' : 'Gider'} />
              <Bar dataKey="income"  fill="#22c55e" radius={[3,3,0,0]} name="income" />
              <Bar dataKey="expense" fill="#ef4444" radius={[3,3,0,0]} name="expense" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// ─── Ayarlar Sekmesi ─────────────────────────────────────────────────────────
function SettingsTab({ categories, onCategoryChange, txTypes, onTxTypesChange }) {
  const [section, setSection] = useState('txTypes');
  const [pms, setPms]         = useState([]);
  const [pmLoading, setPmL]   = useState(false);
  const [catName, setCatName] = useState('');
  const [catType, setCatType] = useState('expense');
  const [catSaving, setCatSaving] = useState(false);
  const pmBlank = { name: '', type: 'card', owner: '' };
  const [pmForm, setPmForm]   = useState(pmBlank);
  const [showPmForm, setShowPmForm] = useState(false);
  // Yeni tür formu
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [newTypeGrp,   setNewTypeGrp]   = useState('gelir');
  const [typeSaving,   setTypeSaving]   = useState(false);

  async function loadPm() { setPmL(true); try { setPms(await cfReq('/payment-methods')); } catch(_){} finally { setPmL(false); } }
  useEffect(() => { if (section === 'pm') loadPm(); }, [section]);

  async function addCat() {
    if (!catName.trim()) return;
    setCatSaving(true);
    try { await cfReq('/categories', { method: 'POST', body: JSON.stringify({ name: catName, type: catType }) }); setCatName(''); onCategoryChange(); }
    catch (e) { alert(e.message); } finally { setCatSaving(false); }
  }
  async function delCat(id) { if (!window.confirm('Sil?')) return; await cfReq(`/categories/${id}`, { method: 'DELETE' }); onCategoryChange(); }
  async function savePm() {
    if (!pmForm.name.trim()) return alert('İsim zorunlu');
    try { await cfReq('/payment-methods', { method: 'POST', body: JSON.stringify(pmForm) }); setPmForm(pmBlank); setShowPmForm(false); loadPm(); }
    catch (e) { alert(e.message); }
  }
  async function togglePm(pm) { await cfReq(`/payment-methods/${pm.id}`, { method: 'PATCH', body: JSON.stringify({ active: !pm.active }) }); loadPm(); }
  async function delPm(id)    { if (!window.confirm('Sil?')) return; await cfReq(`/payment-methods/${id}`, { method: 'DELETE' }); loadPm(); }

  async function addTxType() {
    if (!newTypeLabel.trim()) return;
    setTypeSaving(true);
    try {
      await cfReq('/tx-types', { method: 'POST', body: JSON.stringify({ label: newTypeLabel, grp: newTypeGrp }) });
      setNewTypeLabel(''); onTxTypesChange();
    } catch (e) { alert(e.message); } finally { setTypeSaving(false); }
  }
  async function toggleTxType(t) {
    await cfReq(`/tx-types/${t.key}`, { method: 'PATCH', body: JSON.stringify({ active: !t.active }) });
    onTxTypesChange();
  }
  async function delTxType(t) {
    if (t.is_system) return alert('Sistem türleri silinemez');
    if (!window.confirm(`"${t.label}" türünü sil?`)) return;
    await cfReq(`/tx-types/${t.key}`, { method: 'DELETE' }); onTxTypesChange();
  }

  const SECTIONS = [
    { key: 'txTypes',     label: '🏷️ İşlem Türleri' },
    { key: 'categories',  label: '🗂️ Kategoriler' },
    { key: 'pm',          label: '💳 Ödeme Yöntemleri' },
  ];

  const gelirTypes = txTypes.filter(t => t.grp === 'gelir');
  const giderTypes = txTypes.filter(t => t.grp === 'gider');

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-5">
      {/* Mobil: yatay sekmeler | Masaüstü: sol sidebar */}
      <div className="flex md:flex-col gap-1 md:w-48 md:flex-shrink-0 overflow-x-auto md:overflow-visible">
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setSection(s.key)}
            className={`whitespace-nowrap md:w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex-shrink-0 ${section === s.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-w-0">

        {/* ── İşlem Türleri ── */}
        {section === 'txTypes' && (
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-3">🏷️ İşlem Türleri</h3>
            <p className="text-xs text-gray-400 mb-4">Sistem türleri devre dışı bırakılabilir ama silinemez. Özel türler ekleyip silebilirsiniz.</p>

            {/* Yeni özel tür */}
            <div className="flex gap-2 mb-5">
              <input value={newTypeLabel} onChange={e => setNewTypeLabel(e.target.value)}
                placeholder="Yeni tür adı…"
                onKeyDown={e => e.key === 'Enter' && addTxType()}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              <select value={newTypeGrp} onChange={e => setNewTypeGrp(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="gelir">📈 Gelir</option>
                <option value="gider">📉 Gider</option>
              </select>
              <button onClick={addTxType} disabled={typeSaving || !newTypeLabel.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm">
                + Ekle
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Gelir grubu */}
              <div>
                <div className="text-xs font-semibold text-green-600 mb-2 flex items-center gap-1">📈 Gelir</div>
                <div className="space-y-1.5">
                  {gelirTypes.map(t => (
                    <div key={t.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${t.active ? 'border-gray-100 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                      <span className="flex-1 text-gray-700">{t.label}</span>
                      {t.is_system && <span className="text-xs text-gray-300">sistem</span>}
                      <button onClick={() => toggleTxType(t)}
                        className={`text-xs px-2 py-0.5 rounded-full border ${t.active ? 'bg-green-50 text-green-600 border-green-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                        {t.active ? 'Aktif' : 'Pasif'}
                      </button>
                      {!t.is_system && (
                        <button onClick={() => delTxType(t)} className="text-xs text-red-300 hover:text-red-500">Sil</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {/* Gider grubu */}
              <div>
                <div className="text-xs font-semibold text-red-500 mb-2 flex items-center gap-1">📉 Gider</div>
                <div className="space-y-1.5">
                  {giderTypes.map(t => (
                    <div key={t.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${t.active ? 'border-gray-100 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                      <span className="flex-1 text-gray-700">{t.label}</span>
                      {t.is_system && <span className="text-xs text-gray-300">sistem</span>}
                      <button onClick={() => toggleTxType(t)}
                        className={`text-xs px-2 py-0.5 rounded-full border ${t.active ? 'bg-green-50 text-green-600 border-green-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>
                        {t.active ? 'Aktif' : 'Pasif'}
                      </button>
                      {!t.is_system && (
                        <button onClick={() => delTxType(t)} className="text-xs text-red-300 hover:text-red-500">Sil</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Kategoriler ── */}
        {section === 'categories' && (
          <div>
            <h3 className="text-sm font-semibold text-gray-600 mb-3">🗂️ Kategoriler</h3>
            <div className="flex gap-2 mb-4">
              <input value={catName} onChange={e => setCatName(e.target.value)} placeholder="Yeni kategori…"
                onKeyDown={e => e.key === 'Enter' && addCat()}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              <select value={catType} onChange={e => setCatType(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="income">📈 Gelir</option>
                <option value="expense">📉 Gider</option>
              </select>
              <button onClick={addCat} disabled={catSaving || !catName.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg text-sm">+ Ekle</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {['income','expense'].map(type => (
                <Card key={type} className="p-4">
                  <div className="text-xs font-semibold text-gray-500 mb-2">{type === 'income' ? '📈 Gelir' : '📉 Gider'}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {categories.filter(c => c.type === type).map(c => (
                      <span key={c.id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {c.name}
                        <button onClick={() => delCat(c.id)} className="hover:opacity-60">✕</button>
                      </span>
                    ))}
                    {!categories.filter(c => c.type === type).length && <span className="text-xs text-gray-400">Henüz yok</span>}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* ── Ödeme Yöntemleri ── */}
        {section === 'pm' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-600">💳 Ödeme Yöntemleri</h3>
              <button onClick={() => setShowPmForm(p => !p)} className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">➕ Yeni</button>
            </div>
            {showPmForm && (
              <Card className="p-4 mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-3">
                  <div className="md:col-span-2">
                    <Input label="İsim" value={pmForm.name} onChange={e => setPmForm(p => ({...p, name: e.target.value}))} placeholder="Ziraat Kartı…" />
                  </div>
                  <Select label="Tür" value={pmForm.type} onChange={e => setPmForm(p => ({...p, type: e.target.value}))}>
                    <option value="cash">💵 Nakit</option><option value="card">💳 Kart</option>
                    <option value="bank">🏦 Havale</option><option value="safe">🏛️ Kasa</option>
                  </Select>
                  <Select label="Sahibi" value={pmForm.owner} onChange={e => setPmForm(p => ({...p, owner: e.target.value}))}>
                    <option value="">— Ortak —</option><option>Bilal</option><option>Semih</option>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <button onClick={savePm} className="bg-blue-600 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-blue-700">Ekle</button>
                  <button onClick={() => setShowPmForm(false)} className="border border-gray-200 text-gray-500 px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50">İptal</button>
                </div>
              </Card>
            )}
            {pmLoading ? <div className="text-center py-6 text-gray-400">Yükleniyor…</div> : (
              <div className="space-y-1.5">
                {pms.map(pm => (
                  <div key={pm.id} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border ${pm.active ? 'border-gray-100 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
                    <span>{PM_ICON[pm.type]}</span>
                    <span className="text-sm font-medium text-gray-700 flex-1">{pm.name}</span>
                    {pm.owner && <Badge color="gray">{pm.owner}</Badge>}
                    <button onClick={() => togglePm(pm)} className={`text-xs px-2.5 py-0.5 rounded-full border ${pm.active ? 'bg-green-50 text-green-600 border-green-200' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>{pm.active ? 'Aktif' : 'Pasif'}</button>
                    <button onClick={() => delPm(pm.id)} className="text-xs text-red-300 hover:text-red-500">Sil</button>
                  </div>
                ))}
                {!pms.length && <p className="text-xs text-gray-400 text-center py-4">Henüz ödeme yöntemi yok</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function CompanyFinance() {
  const [tab, setTab]               = useState('ledger');
  const [categories, setCategories] = useState([]);
  const [txTypes,    setTxTypes]    = useState(DEFAULT_TX_TYPES); // fallback — API gelince override edilir

  async function loadCats()    { try { setCategories(await cfReq('/categories')); } catch (_) {} }
  async function loadTxTypes() {
    try {
      const data = await cfReq('/tx-types');
      if (Array.isArray(data) && data.length > 0) setTxTypes(data);
    } catch (_) {} // sessiz — DEFAULT_TX_TYPES devrede kalır
  }

  useEffect(() => { loadCats(); loadTxTypes(); }, []);

  const TABS = [
    { key: 'overview', label: '📊 Genel Bakış' },
    { key: 'ledger',   label: '📒 Defter'       },
    { key: 'settings', label: '⚙️ Ayarlar'      },
  ];

  return (
    <div className="p-3 sm:p-5 md:p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">🏢 Muhasebe Defteri</h1>
        <p className="text-sm text-gray-400 mt-0.5">BSW Teknoloji</p>
      </div>
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'overview' && <OverviewTab />}
      {tab === 'ledger'   && <LedgerTab txTypes={txTypes} categories={categories} />}
      {tab === 'settings' && (
        <SettingsTab
          categories={categories}
          onCategoryChange={loadCats}
          txTypes={txTypes}
          onTxTypesChange={loadTxTypes}
        />
      )}
    </div>
  );
}
